import katex from 'katex';
import { decode } from 'html-entities';
import AsciiMathParser from 'asciimath2tex';

// Implementation referenced from @widcardw/markdown-it-asciimath

function texToHtmlInline(tex: string) {
  try {
    return katex.renderToString(decode(tex), { throwOnError: false });
  } catch (err) {
    return `<pre style="white-space: normal; background-color: #7f7f7f18; padding: 0.5rem;">${err}</pre>`;
  }
}

function tokenToHtmlInline(tokens: any[], idx: number) {
  return texToHtmlInline(tokens[idx].content);
}

const AmIt: any = (md: any, options: AmItOptions = {}) => {
  const o = resolveOptions(options);

  /**
   * asciimath block
   */
  amBlockRenderer(md, o);

  /**
   * asciimath inline
   */
  // In fact, second param here can be inconsistent with the `md.renderer.rules.am_inline`,
  //                                                                            ^^^^^^^^^
  // but must be relevant with the expression `token = state.push('am_inline', 'math', 0)`.
  //                                                               ^^^^^^^^^
  md.inline.ruler.after('escape', 'am_inline', amInlineGenerator(o));
  md.renderer.rules.am_inline = tokenToHtmlInline;
};

const am = new AsciiMathParser();

function amBlockRenderer(md: any, o: RestrictAmItOptions) {
  const temp = md.renderer.rules.fence!.bind(md.renderer.rules);
  md.renderer.rules.fence = (tokens: any[], index: number, options: any, env: any, slf: any) => {
    const token = tokens[index];

    // recognize ```asciimath or ```am
    //              ^^^^^^^^^       ^^
    if (o.block.includes(token.info.trim())) {
      try {
        const content = token.content.trim();
        const tex = am.parse(decode(content));
        return `<p>${katex.renderToString(tex, { displayMode: true, throwOnError: false })}</p>`;
      } catch (err) {
        return `<pre style="white-space: normal; background-color: #7f7f7f18; padding: 0.5rem;">${err}</pre>`;
      }
    }
    return temp(tokens, index, options, env, slf);
  };
}

function amInlineGenerator(o: RestrictAmItOptions) {
  return (state: any, silent: boolean) => {
    // End of line
    if (state.pos + 2 >= state.posMax) return false;

    const { open, close } = o.inline;
    // ignore escapes not start with `options.inline.open`
    if (state.src.slice(state.pos, state.pos + 2) !== open) return false;

    // ``x/y``
    //   ^- start
    const start = state.pos + open.length;

    let match = start;
    let pos: number;
    let token: any;

    // borrowed from markdown-it-katex
    // eslint-disable-next-line no-cond-assign
    while ((match = state.src.indexOf(close, match)) !== -1) {
      pos = match - 1;
      while (state.src[pos] === '\\') {
        pos -= 1;
      }

      // Even number of escapes, potential closing delimiter found
      if ((match - pos) % 2 === 1) break;

      match += 1;
    }

    if (start >= match) return false;

    // does not match
    if (match === -1) {
      state.pos = start;
      return false;
    }

    // render inline tex
    if (!silent) {
      const content = state.src.slice(start, match);
      const tex = am.parse(decode(content));

      // push token `am_inline` into token stream
      // which is schedule to be processed by the rule `am_inline`
      token = state.push('math_inline', 'math', 0);
      token.markup = '$';
      token.content = tex;
    }

    // process offset
    state.pos = match + close.length;

    return true;
  };
}

interface AmItOptions {
  /**
   * Boundary of inline ascii math
   *
   * @default { open: '``', close: '``' }
   */
  inline?: {
    open: string;
    close: string;
  };
  /**
   * Token info of codeblock
   *
   * @default ['am', 'asciimath']
   */
  block?: string | string[];
}

interface RestrictAmItOptions extends Required<AmItOptions> {
  block: string[];
}

function resolveOptions(options: AmItOptions): RestrictAmItOptions {
  const resolved: RestrictAmItOptions /* default options */ = {
    inline: {
      open: '``',
      close: '``',
    },
    block: ['asciimath', 'am'],
  };

  if (options.inline?.open?.trim()) resolved.inline.open = options.inline.open.trim();

  if (options.inline?.close?.trim()) resolved.inline.close = options.inline.close.trim();

  if (options.block) {
    if (typeof options.block === 'string') resolved.block = [options.block];
    else if (Array.isArray(options.block) && options.block.length >= 1)
      resolved.block = options.block;
  }

  return resolved;
}

export default AmIt;
